import { Box, Button, Typography } from '@mui/material';
import { BookingFlowShell, type BookingFlowShellConfig } from '../../../booking-flow/BookingFlowShell';
import { rentCarFlowConfig, type RentCarFlowState } from '../flowConfig';
import { rentCarResumeAdapter } from '../resume';
import { SearchFormStep } from '../components/SearchFormStep';
import { VehicleResultsStep } from '../components/VehicleResultsStep';
import { DriverFormStep } from '../components/DriverFormStep';
import { PaymentForm } from '../../../components/payment/PaymentForm';
import { SendForApprovalPanel } from '../../../components/booking/SendForApprovalPanel';
import { ApprovalPassengerStep } from '../../../components/booking/ApprovalPassengerStep';
import {
  approvalEmployeeId,
  corporateMaxBudget,
  needsCorporateApproval,
} from '../../../components/booking/corporateApproval';
import { DocumentStep } from '../components/DocumentStep';

function rentCarApprovalActive(state: RentCarFlowState, sessionEmployeeId: number | null) {
  const gate = { cart: state.cart, searchEmployeeId: state.searchRequest?.employeeID };
  const employeeId = approvalEmployeeId(gate, sessionEmployeeId);
  if (!needsCorporateApproval(gate) || !employeeId) return null;
  return { employeeId, maxBudget: corporateMaxBudget(gate) };
}

const shellConfig: BookingFlowShellConfig<RentCarFlowState> = {
  title: 'Araç Kiralama',
  description: 'Arama → Rezervasyona Alma → Sürücü Bilgisi → Ön Rezervasyon → Kesinleştirme → Ödeme → Belge',
  flowConfig: rentCarFlowConfig,
  resumeAdapter: rentCarResumeAdapter,
  sentForApproval: {
    isSent: (s) => !!s.sentForApproval,
    passengerStepId: 'driver',
    reservationPath: '/rent-car/reservations',
    message:
      'Araç kiralama onaya gönderilmiştir. Yöneticiniz onayladığında rezervasyon listenizden ödemeye devam edebilirsiniz.',
  },
  approvalGate: {
    passengerStepId: 'driver',
    isActive: rentCarApprovalActive,
    render: ({ flow, running, employeeId, maxBudget }) => (
      <ApprovalPassengerStep
        employeeId={employeeId}
        maxBudget={maxBudget}
        productLabel="araç kiralama"
        loading={running}
        onSubmit={(passengers, contact) => {
          flow
            .runStep({ driver: { passenger: passengers[0], contact }, sentForApproval: true })
            .catch(() => undefined);
        }}
      />
    ),
  },
  renderStep: {
    search: ({ flow, running }) => (
      <SearchFormStep
        loading={running}
        error={flow.status === 'error' ? flow.error ?? undefined : undefined}
        onSubmit={(searchRequest) => {
          flow.runStep({ searchRequest }).catch(() => undefined);
        }}
      />
    ),
    select: ({ flow, running }) =>
      flow.state.searchResult ? (
        <VehicleResultsStep
          searchResult={flow.state.searchResult}
          loading={running}
          onSelect={(vehicle) => {
            flow.runStep({ selectedVehicle: vehicle }).catch(() => undefined);
          }}
          onBackToSearch={() => flow.goToStep(0)}
        />
      ) : null,
    driver: ({ flow, running }) => (
      <DriverFormStep
        loading={running}
        error={flow.status === 'error' ? flow.error ?? undefined : undefined}
        onSubmit={(driver) => {
          flow.runStep({ driver }).catch(() => undefined);
        }}
      />
    ),
    prebooking: ({ runStep, running }) => (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography sx={{ mb: 2 }}>Ön rezervasyon adımına devam edin.</Typography>
        <Button variant="contained" disabled={running} onClick={() => runStep()}>
          {running ? 'İşleniyor…' : 'Ön Rezervasyon Yap'}
        </Button>
      </Box>
    ),
    finalize: ({ runStep, running }) => (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography sx={{ mb: 2 }}>Rezervasyonu kesinleştirin.</Typography>
        <Button variant="contained" disabled={running} onClick={() => runStep()}>
          {running ? 'İşleniyor…' : 'Kesinleştir'}
        </Button>
      </Box>
    ),
    payment: ({ flow, running }) => (
      <>
        <SendForApprovalPanel product="RentCar" cart={flow.state.cart ?? undefined} />
        <PaymentForm
          amount={flow.state.cart?.remainingTotal}
          currency={flow.state.selectedVehicle?.currency ?? undefined}
          loading={running}
          shoppingCartId={flow.state.shoppingCartId}
          onSubmit={({ paymentForm }) => {
            flow.runStep({ paymentForm }).catch(() => undefined);
          }}
        />
      </>
    ),
    document: ({ flow }) => (
      <DocumentStep
        shoppingCartId={flow.state.shoppingCartId}
        cart={flow.state.cart}
        payment={flow.state.payment}
        onStartOver={flow.reset}
      />
    ),
  },
};

export default function RentCarFlowPage() {
  return <BookingFlowShell config={shellConfig} />;
}
